// =========================================
// subscriptionHelpers — logica abbonamento (pura, testabile)
// =========================================
//
// Single source of truth per lo stato dell'abbonamento di un tenant.
// L'oggetto `subscription` vive su `tenants/{tenantId}.subscription`:
//   { plan, subscriptionStart, subscriptionEnd, autoRenewal, modules, updatedAt }
//
// Modello UNIFICATO: ogni piano (Demo incluso) usa la stessa coppia
// subscriptionStart / subscriptionEnd. La fase (trial/active/expired) è SEMPRE
// derivata qui — niente campo persistito.

import { PLANS, PLATFORM_MODULES, MODULE_OF_VIEW } from './catalogs';
import { toJsDate, MS_PER_DAY } from './dateHelpers';

// Giorni aggiunti a subscriptionStart in base al ciclo di rinnovo (billingCycle).
//   demo    → 0   (nessun rinnovo: il periodo di prova ha durata a parte, 7gg)
//   monthly → 30
//   annual  → 365
export const BILLING_CYCLE_DAYS = {
  demo:    0,
  monthly: 30,
  annual:  365,
};

// Durata del periodo di prova Demo (gestita separatamente dal billingCycle).
export const DEMO_TRIAL_DAYS = 7;

// Sconto applicato al totale annuo rispetto a 12 mensilità.
export const ANNUAL_DISCOUNT = 0.10;

/** Entry del catalogo piani dato l'id, o null. */
export const getPlan = (planId) => PLANS.find((p) => p.id === planId) || null;

// ── Moduli-area (anagrafiche / logistica / commerciale) ─────────────────────

/** Entry catalogo modulo-area, o null. */
export const getModule = (moduleId) => PLATFORM_MODULES.find((m) => m.id === moduleId) || null;

/**
 * Un modulo-area è attivo per questo subscription?
 *   - Anagrafiche (core) → SEMPRE true.
 *   - Piano demo (trial) → tutti i moduli attivi (accesso pieno alla prova).
 *   - Altrimenti → flag esplicito in subscription.modules.
 * Tollerante a subscription null/undefined (tenant pre-feature → trattato come demo).
 */
export const isModuleActive = (subscription, moduleId) => {
  if (getModule(moduleId)?.core) return true;          // anagrafiche
  const plan = subscription?.plan || 'demo';
  if (plan === 'demo') return true;                    // trial = tutto sbloccato
  return !!subscription?.modules?.[moduleId];
};

/** Modulo che sblocca una view (id sidebar/router), o null se è CORE. */
export const moduleForView = (viewId) => MODULE_OF_VIEW[viewId] || null;

/** True se la view è accessibile (core, oppure modulo attivo). */
export const isViewAccessible = (subscription, viewId) => {
  const mod = moduleForView(viewId);
  return !mod || isModuleActive(subscription, mod);
};

/** Dipendenze di un modulo (es. logistica → ['anagrafiche']). */
export const moduleDeps = (moduleId) => getModule(moduleId)?.deps || [];

/**
 * Canone mensile = Anagrafiche (sempre) + moduli-area attivi. In ciclo annuale
 * applica lo sconto. Ritorna null se prezzo "su richiesta" (Ultra), 0 per Demo.
 */
export const computeMonthlyTotal = (planId, modules, cycle = 'monthly') => {
  const mp = getPlan(planId)?.modulePrices;
  if (!mp) return null;                                // Ultra / su richiesta
  let monthly = mp.anagrafiche;                        // core sempre incluso
  if (modules?.logistica)   monthly += mp.logistica;
  if (modules?.commerciale) monthly += mp.commerciale;
  if (cycle === 'annual') return Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT));
  return monthly;
};

/** Numero massimo di veicoli per un piano (Infinity per demo/ultra, 0 se ignoto). */
export const getMaxVehicles = (planId) => getPlan(planId)?.maxVehicles ?? 0;

/** True se la creazione di un nuovo veicolo supererebbe il limite del piano. */
export const isVehicleLimitReached = (planId, currentCount) =>
  currentCount >= getMaxVehicles(planId);

/** Numero massimo di clienti per un piano (Infinity per demo/ultra, 0 se ignoto). */
export const getMaxClients = (planId) => getPlan(planId)?.maxClients ?? 0;

/** True se la creazione di un nuovo cliente supererebbe il limite del piano. */
export const isClientLimitReached = (planId, currentCount) =>
  currentCount >= getMaxClients(planId);

/**
 * True se un piano può essere attivato con i conteggi attuali (guard downgrade).
 * Considera sia i veicoli sia i clienti. `currentClientCount` è opzionale per
 * retro-compatibilità con i chiamanti che passano solo i veicoli.
 */
export const canActivatePlan = (targetPlanId, currentVehicleCount, currentClientCount = 0) =>
  currentVehicleCount <= getMaxVehicles(targetPlanId) &&
  currentClientCount <= getMaxClients(targetPlanId);

/** Quanti veicoli eliminare per poter attivare il piano target (0 se già OK). */
export const vehiclesToRemove = (targetPlanId, currentVehicleCount) =>
  Math.max(0, currentVehicleCount - getMaxVehicles(targetPlanId));

/** Quanti clienti eliminare per poter attivare il piano target (0 se già OK). */
export const clientsToRemove = (targetPlanId, currentClientCount) =>
  Math.max(0, currentClientCount - getMaxClients(targetPlanId));

/**
 * Prezzo del piano per il ciclo scelto, arrotondato all'euro.
 *   - mensile → priceMonthly
 *   - annuale → priceMonthly × 12 × (1 − sconto)  (totale annuo)
 * Ritorna null se il prezzo è "su richiesta" (Ultra), 0 se gratis (Demo).
 */
export const planPrice = (planId, cycle) => {
  const m = getPlan(planId)?.priceMonthly;
  if (m == null) return null;                 // su richiesta
  if (cycle === 'annual') return Math.round(m * 12 * (1 - ANNUAL_DISCOUNT));
  return m;                                    // mensile (Demo = 0)
};

/** Risparmio annuo (€) rispetto a 12 mensilità. 0 se gratis o su richiesta. */
export const annualSaving = (planId) => {
  const m = getPlan(planId)?.priceMonthly;
  if (!m) return 0;
  return Math.round(m * 12 * ANNUAL_DISCOUNT);
};

/**
 * Calcola la data di fine periodo per un piano a PAGAMENTO dato l'inizio e il
 * ciclo di rinnovo. Per il Demo non si usa questa funzione: la durata di prova
 * (7gg) è gestita da ensureTrialStarted con DEMO_TRIAL_DAYS.
 * @param startMs inizio in ms
 * @param cycle   'monthly' | 'annual' (demo → 0 giorni)
 * @returns Date di fine
 */
export const computeSubscriptionEnd = (startMs, cycle) =>
  new Date(startMs + (BILLING_CYCLE_DAYS[cycle] ?? 0) * MS_PER_DAY);

/**
 * Calcola lo stato dell'abbonamento a runtime.
 * @param subscription oggetto subscription del tenant (può essere null/undefined)
 * @param nowMs        timestamp corrente in ms (Date.now())
 * @returns { plan, phase, daysRemaining, isBlocked, subscriptionEnd }
 *   phase: 'trial' | 'active' | 'expired' (derivata)
 *   daysRemaining: giorni interi residui (0 se scaduto/non configurato)
 *   isBlocked: true se l'accesso va negato finché non si rinnova
 */
export function computeSubscriptionState(subscription, nowMs) {
  const plan = subscription?.plan || 'demo';
  const end  = toJsDate(subscription?.subscriptionEnd);

  // Non ancora configurato: demo prima della verifica email, o tenant pre-feature.
  // Non blocca: il periodo verrà avviato (ensureTrialStarted) alla verifica.
  if (!end) {
    return {
      plan,
      phase: plan === 'demo' ? 'trial' : 'active',
      daysRemaining: plan === 'demo' ? DEMO_TRIAL_DAYS : 0,
      isBlocked: false,
      subscriptionEnd: null,
    };
  }

  const msLeft  = end.getTime() - nowMs;
  const expired = msLeft <= 0;
  return {
    plan,
    phase: expired ? 'expired' : (plan === 'demo' ? 'trial' : 'active'),
    daysRemaining: expired ? 0 : Math.ceil(msLeft / MS_PER_DAY),
    isBlocked: expired,
    subscriptionEnd: end,
  };
}
